import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { getExecutionRunById } from '@/features/executionRuns/data/executionRuns';
import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { queryKeys } from '@/shared/hooks';
import { Box } from '@/ui';
import {
  mapSuiteRunSummariesWithRunNumbers,
  mapTestRunSummariesWithRunNumbers,
} from '@/ui/composites/table';
import type { ExecutionRunDto } from '@flowqa/shared';

type ExecutionRunsBreakdownKind = 'suite' | 'test';

const countExecutionRunBreakdownTotal = (
  row: ExecutionRunDto,
  kind: ExecutionRunsBreakdownKind,
): number => {
  if (kind === 'suite') {
    return (
      (row.suiteRunPassed ?? 0) +
      (row.suiteRunFailed ?? 0) +
      (row.suiteRunErrored ?? 0) +
      (row.suiteRunSkipped ?? 0) +
      (row.suiteRunUntested ?? 0)
    );
  }

  return (
    (row.testRunPassed ?? 0) +
    (row.testRunFailed ?? 0) +
    (row.testRunErrored ?? 0) +
    (row.testRunSkipped ?? 0) +
    (row.testRunUntested ?? 0)
  );
};

type ExecutionRunBreakdownCellProps = {
  kind: ExecutionRunsBreakdownKind;
  groupErroredWithFailed?: boolean;
  organisationId?: string | null;
  row: ExecutionRunDto;
};

export const ExecutionRunBreakdownCell = ({
  groupErroredWithFailed,
  kind,
  organisationId,
  row,
}: ExecutionRunBreakdownCellProps): ReactElement => {
  const [detailsRequested, setDetailsRequested] = useState(false);
  const existingSummaryCount =
    kind === 'suite' ? (row.suiteRunSummaries?.length ?? 0) : (row.testRunSummaries?.length ?? 0);
  const shouldFetchDetails =
    detailsRequested &&
    existingSummaryCount === 0 &&
    countExecutionRunBreakdownTotal(row, kind) > 0;
  const detailQuery = useQuery({
    queryKey: queryKeys.executionRunDetail(row.id, organisationId ?? null),
    enabled: shouldFetchDetails,
    staleTime: 30_000,
    queryFn: async () => await getExecutionRunById(row.id, organisationId ?? undefined),
  });
  const detail = detailQuery.data;
  const requestDetails = (): void => {
    setDetailsRequested(true);
  };

  if (kind === 'suite') {
    return (
      <Box onFocus={requestDetails} onMouseEnter={requestDetails}>
        <TableStatusBreakdownCell
          entity="suite"
          counts={{
            passed: row.suiteRunPassed ?? 0,
            failed: row.suiteRunFailed ?? 0,
            errored: row.suiteRunErrored ?? 0,
            skipped: row.suiteRunSkipped ?? 0,
            untested: row.suiteRunUntested ?? 0,
          }}
          groupErroredWithFailed={groupErroredWithFailed}
          summaryItems={() =>
            mapSuiteRunSummariesWithRunNumbers(
              existingSummaryCount > 0 ? row.suiteRunSummaries : detail?.suiteRunSummaries,
            )
          }
        />
      </Box>
    );
  }

  return (
    <Box onFocus={requestDetails} onMouseEnter={requestDetails}>
      <TableStatusBreakdownCell
        entity="test"
        counts={{
          passed: row.testRunPassed ?? 0,
          failed: row.testRunFailed ?? 0,
          errored: row.testRunErrored ?? 0,
          skipped: row.testRunSkipped ?? 0,
          untested: row.testRunUntested ?? 0,
        }}
        groupErroredWithFailed={groupErroredWithFailed}
        summaryItems={() =>
          mapTestRunSummariesWithRunNumbers(
            existingSummaryCount > 0 ? row.testRunSummaries : detail?.testRunSummaries,
            'full',
          )
        }
      />
    </Box>
  );
};
